import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { MapPin } from 'lucide-react';
import { aboutContent } from '../mock';

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">About Regenr8 International</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Empowering brands and manufacturers to achieve sustainable global expansion
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 md:p-12 border-2 border-blue-100">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Who We Are</h2>
            <p className="text-lg text-gray-700 leading-relaxed text-center mb-4">
              We help ambitious brands achieve successful market entry and global expansion making growth not just possible but profitable.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed text-center mb-4">
              Our team turns uncertainty into strategic opportunities by providing sales management, strategic sourcing and business development solutions tailored for FMCG, retail and consumer brands.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed text-center">
              Whether you're targeting emerging markets or scaling globally, we partner with you to accelerate success.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {aboutContent.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {aboutContent.vision}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Core Values */}
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 text-center">Our Core Values</h2>
            <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
              The principles that guide every partnership and drive our commitment to your success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {aboutContent.values.map((value, index) => (
              <Card key={index} className="border-2 hover:border-blue-900 transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-900 font-bold text-xl">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Leadership Team</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Meet the experienced professionals driving global expansion for brands worldwide
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.team.map((member, index) => (
              <Card key={index} className="border-2 hover:border-blue-900 transition-all duration-300 hover:shadow-xl group overflow-hidden">
                <CardContent className="p-0">
                  <div className="aspect-[3/4] overflow-hidden bg-gray-100">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover object-top grayscale hover:grayscale-0 transition-all duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {member.name}
                    </h3>
                    <p className="text-blue-900 font-medium mb-2">
                      {member.role}
                    </p>
                    <div className="flex items-center justify-center space-x-1 text-gray-600">
                      <MapPin size={16} />
                      <p className="text-sm">
                        {member.location}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
